/**
 * The options a record field's status cell offers.
 *
 * A user-defined status column carries its own options in the saved layout. A
 * record field's set belongs to the platform instead: a project's status is one
 * of the handful the projects service accepts, so the board reads them from the
 * domain rather than keeping an editable copy that could drift out of agreement
 * with what a write is allowed to say.
 *
 * The whole set is listed, not the values the loaded page happens to show, so a
 * Kanban lane exists for a status nothing is in yet - which is the lane a card
 * most often has to be dragged into.
 */
import type {
  AssetWorkflowResponseDto,
  ProjectFileResponseDto,
} from '@inference.engine/workspace-api-client/web';

import {
  PROJECT_STATUS_MAP,
  type ProjectStatus,
  type ProjectStatusVariant,
} from '@/entities/project';
import { i18n } from '@/shared/i18n';

import { STATUS_COLORS, type StatusOption } from './board-columns';

/**
 * Read off the board's own palette, so a platform status and a status a user
 * made on the same board read as one vocabulary rather than two, and both are
 * covered by the palette's contrast test.
 */
const VARIANT_COLOR: Record<ProjectStatusVariant, string> = {
  neutral: STATUS_COLORS.slate,
  muted: STATUS_COLORS.slateDark,
  info: STATUS_COLORS.blue,
  warning: STATUS_COLORS.amber,
  success: STATUS_COLORS.green,
  destructive: STATUS_COLORS.red,
};

/** Called, not held: `PROJECT_STATUS_MAP`'s labels are getters, not frozen text. */
function projectStatusOptions(): StatusOption[] {
  return Object.entries(PROJECT_STATUS_MAP).map(([id, { label, variant }]) => ({
    id: id as ProjectStatus,
    label,
    color: VARIANT_COLOR[variant],
  }));
}

/**
 * How one status of a wire enum is drawn. The label is a function so it
 * resolves against the active locale each time the options are read.
 */
interface StatusStyle {
  label: () => string;
  color: string;
}

/**
 * Keyed by the union the server sends, so the set stays exhaustive: a status
 * added or dropped on the wire fails to compile until the board says how to
 * draw it, rather than reaching a person as a lane of unstyled raw enum.
 */
const ASSET_REVIEW_STATUS: Record<ProjectFileResponseDto['status'], StatusStyle> = {
  to_review: {
    label: () => i18n.t('trackers:trackersBoard.recordStatusOptions.reviewToReview'),
    color: STATUS_COLORS.amber,
  },
  approved: {
    label: () => i18n.t('trackers:trackersBoard.recordStatusOptions.reviewApproved'),
    color: STATUS_COLORS.green,
  },
  rejected: {
    label: () => i18n.t('trackers:trackersBoard.recordStatusOptions.reviewRejected'),
    color: STATUS_COLORS.red,
  },
  not_applicable: {
    label: () => i18n.t('trackers:trackersBoard.recordStatusOptions.reviewNotApplicable'),
    color: STATUS_COLORS.slateDark,
  },
};

/** How far the approval engine has taken an asset through its chain. */
const ASSET_APPROVAL_STATUS: Record<AssetWorkflowResponseDto['status'], StatusStyle> = {
  not_started: {
    label: () => i18n.t('trackers:trackersBoard.recordStatusOptions.approvalNotStarted'),
    color: STATUS_COLORS.slate,
  },
  in_progress: {
    label: () => i18n.t('trackers:trackersBoard.recordStatusOptions.approvalInProgress'),
    color: STATUS_COLORS.blue,
  },
  completed: {
    label: () => i18n.t('trackers:trackersBoard.recordStatusOptions.approvalCompleted'),
    color: STATUS_COLORS.green,
  },
};

function fromStatusTable(table: Record<string, StatusStyle>): () => StatusOption[] {
  return () =>
    Object.entries(table).map(([id, { label, color }]) => ({ id, label: label(), color }));
}

/**
 * `Partial` at both levels is what makes the lookup honest: an index signature
 * otherwise types every source and every field as present, and most fields on
 * any source are not status fields at all.
 */
const OPTIONS_BY_SOURCE: Readonly<
  Partial<Record<string, Partial<Record<string, () => StatusOption[]>>>>
> = {
  projects: { status: projectStatusOptions },
  assets: {
    reviewStatus: fromStatusTable(ASSET_REVIEW_STATUS),
    approvalStatus: fromStatusTable(ASSET_APPROVAL_STATUS),
  },
};

/** The options for one field, empty when its source declares none. */
export function recordStatusOptions(
  itemSource: string | null | undefined,
  fieldId: string,
): StatusOption[] {
  return OPTIONS_BY_SOURCE[itemSource ?? '']?.[fieldId]?.() ?? [];
}
